import { useState, useEffect } from 'react';
import { getStorage, ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { getAuth } from 'firebase/auth';

export const useWidgetEdit = (widget, onUpdate) => {
    const [editData, setEditData] = useState({ ...widget });
    // ✅ FIREBASE-FIX: Fallback für undefined position
    const [position, setPosition] = useState(widget.position || 0);
    const auth = getAuth();

    // BIDIREKTIONALE SYNC: Edit-Panel reagiert auf externe Widget-Updates
    useEffect(() => {
        console.log('🔄 useWidgetEdit: Syncing widget data:', {
            widgetId: widget.id,
            widgetType: widget.type,
            height: widget.height,
            heightMobile: widget.heightMobile,
            layoutType: widget.layoutType,
            position: widget.position
        });
        setEditData({ ...widget });
        // ✅ FIREBASE-FIX: Fallback für undefined position
        setPosition(widget.position || 0);
    }, [widget]);

    // Standard handleInputChange - EXAKT wie in bestehenden Komponenten
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        // ⚠️ FIREBASE-FIX: Prüfe auf leere oder ungültige Feldnamen
        if (!name || name.trim() === '') {
            console.error('❌ useWidgetEdit: Invalid field name detected:', { name, value, type });
            return;
        }
        
        const updatedValue = type === 'checkbox' ? checked : value;
        
        // ✅ FIREBASE-SAFE: Prüfe auf undefined/null Werte
        if (updatedValue === undefined || updatedValue === null) {
            console.error('❌ useWidgetEdit: Undefined/null value detected:', { name, value: updatedValue, type });
            return;
        }
        
        const updatedEditData = { ...editData, [name]: updatedValue };
        
        console.log('🔧 useWidgetEdit: Updating field:', { name, value: updatedValue });
        
        setEditData(updatedEditData);
        
        // ✅ FIREBASE-FIX: Sicherstellen dass position nie undefined ist
        const safePosition = position !== undefined && position !== null ? position : 0;
        
        // Speichern Sie die aktualisierten Daten sofort in der Datenbank
        onUpdate({ ...updatedEditData, position: safePosition });
    };

    // Standard uploadImage Funktion - EXAKT wie in bestehenden Komponenten
    const uploadImage = (file, fieldName, setPreview, setUploadingState) => {
        if (!file || file.size > 2 * 1024 * 1024) {
            alert('File is too big (max. 2MB).');
            return;
        }

        setUploadingState(true);
        const storage = getStorage();
        const userId = auth.currentUser.uid;
        const imageRef = storageRef(storage, `pages/${userId}/${widget.id}/${fieldName}_${file.name}`);
        const uploadTask = uploadBytesResumable(imageRef, file);

        uploadTask.on('state_changed', 
            (snapshot) => {
                // Optional: Upload-Fortschritt anzeigen
            }, 
            (error) => {
                console.error('Fehler beim Hochladen:', error);
                setUploadingState(false);
            }, 
            () => {
                getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
                    const updatedEditData = { ...editData, [fieldName]: downloadURL };
                    setEditData(updatedEditData);
                    setPreview(downloadURL);
                    setUploadingState(false);

                    // ✅ FIREBASE-FIX: Sicherstellen dass position nie undefined ist
                    const safePosition = position !== undefined && position !== null ? position : 0;
                    
                    // Speichern Sie die aktualisierten Daten sofort in der Datenbank
                    onUpdate({ ...updatedEditData, position: safePosition });
                });
            }
        );
    };

    // Slider Change Handler
    const handleSliderChange = (name, value) => {
        // ⚠️ FIREBASE-FIX: Prüfe auf leere oder ungültige Feldnamen
        if (!name || name.trim() === '') {
            console.error('❌ useWidgetEdit: Invalid slider field name detected:', { name, value });
            return;
        }
        
        // ✅ FIREBASE-SAFE: Prüfe auf undefined/null Werte
        if (value === undefined || value === null) {
            console.error('❌ useWidgetEdit: Undefined/null slider value detected:', { name, value });
            return;
        }
        
        console.log('🎛️ useWidgetEdit: Updating slider field:', { name, value });
        
        const updatedEditData = { ...editData, [name]: value };
        setEditData(updatedEditData);
        
        // ✅ FIREBASE-FIX: Sicherstellen dass position nie undefined ist
        const safePosition = position !== undefined && position !== null ? position : 0;
        onUpdate({ ...updatedEditData, position: safePosition });
    };

    return {
        editData,
        setEditData,
        position,
        setPosition,
        handleInputChange,
        uploadImage,
        handleSliderChange,
        auth
    };
};
